import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Card, CardContent, makeStyles, Typography } from '@material-ui/core';
import AgentNetButton from '../button/AgentNetButton';
import { pxToRem } from '@fluentsms/agentnet-web-components';

interface PaymentCardProps {
  ePayId?: string;
  ePayName?: string;
  ePayType?: string;
  ePayNickName?: string;
  accountType: string;
  cardEnding?: string;
  goodThru?: string;
  onEdit?: (accountAliasId: string, isAch: boolean) => void;
  onDelete?: (accountAliasId: string, isAch: boolean, accountName: string, paymentType: string) => void;
  accountAliasId: string;
}

const useStyles = makeStyles((theme) => ({
  cardWrap: {
    boxShadow: 'none',
    border: '1px solid #c4c4c4',
    minWidth: 320,
    maxWidth: 640,
    transition: '.3s all',
    '&:hover': {
      borderColor: theme.palette.action.disabled,
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    '& svg': {
      height: 48,
    },
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  mb1: {
    marginBottom: theme.spacing(1),
  },
  editBtn: {
    padding: '4px 10px',
    fontSize: pxToRem(13),
  },
  error: {
    color: 'red',
  },
  accEnding: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  deleteBtn: {
    marginLeft: '8px',
  },
}));

export const VisaCard = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 70 48">
      <rect width="69" height="47" x="0.5" y="0.5" fill="#fff" stroke="#C4C4C4" rx="5.5"></rect>
      <path
        fill="#172B85"
        fillRule="evenodd"
        d="M21.25 32.517h-4.24l-3.18-12.132c-.151-.558-.472-1.052-.943-1.284-1.176-.584-2.473-1.05-3.887-1.284v-.467h6.831c.943 0 1.65.701 1.768 1.516l1.65 8.751 4.239-10.267h4.122l-6.36 15.166zm8.718 0h-4.005L29.26 17.35h4.005l-3.297 15.166zm8.479-10.966c.118-.816.825-1.284 1.65-1.284 1.296-.117 2.708.118 3.887.7l.707-3.265A10.138 10.138 0 0041.039 17c-3.887 0-6.715 2.1-6.715 5.017 0 2.218 2.003 3.382 3.418 4.084 1.53.7 2.119 1.166 2.001 1.866 0 1.05-1.178 1.517-2.355 1.517-1.414 0-2.828-.35-4.123-.935l-.707 3.268c1.414.582 2.944.817 4.359.817 4.358.115 7.067-1.984 7.067-5.134 0-3.967-5.537-4.2-5.537-5.949zM58 32.517L54.82 17.35h-3.416c-.707 0-1.414.467-1.65 1.166l-5.888 14h4.123l.823-2.216h5.065l.472 2.216H58zm-6.006-11.083l1.176 5.716h-3.298l2.122-5.716z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
};

export const MasterCard = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 70 48">
      <rect width="69" height="47" x="0.5" y="0.5" fill="#fff" stroke="#C4C4C4" rx="5.5"></rect>
      <path
        fill="#ED0006"
        fillRule="evenodd"
        d="M35.5 34.314a13.502 13.502 0 01-8.852 3.298C19.11 37.612 13 31.43 13 23.806 13 16.181 19.11 10 26.648 10c3.378 0 6.469 1.242 8.852 3.298A13.502 13.502 0 0144.352 10C51.89 10 58 16.181 58 23.806c0 7.625-6.11 13.806-13.648 13.806a13.505 13.505 0 01-8.852-3.298z"
        clipRule="evenodd"
      ></path>
      <path
        fill="#F9A000"
        fillRule="evenodd"
        d="M35.5 34.314a13.841 13.841 0 004.795-10.508A13.84 13.84 0 0035.5 13.298 13.502 13.502 0 0144.352 10C51.89 10 58 16.181 58 23.806c0 7.625-6.11 13.806-13.648 13.806a13.505 13.505 0 01-8.852-3.298z"
        clipRule="evenodd"
      ></path>
      <path
        fill="#FF5E00"
        fillRule="evenodd"
        d="M35.5 13.298a13.841 13.841 0 014.795 10.508c0 4.208-1.86 7.976-4.795 10.508a13.841 13.841 0 01-4.795-10.508c0-4.208 1.86-7.976 4.795-10.508z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
};

export const ACH = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 70 48">
      <rect width="69" height="47" x="0.5" y="0.5" fill="#fff" stroke="#D9D9D9" rx="5.5"></rect>
      <path
        fill="#036A51"
        fillRule="evenodd"
        d="M17.313 14h-1.236a.102.102 0 00-.103.104v2.195a.101.101 0 01-.064.095l-1.515.64a.106.106 0 01-.114-.023l-1.57-1.571a.103.103 0 00-.146 0l-.875.875a.103.103 0 000 .145l2.916 2.916c.037.037.093.04.135.011a5.951 5.951 0 012.588-1.064.102.102 0 00.088-.102v-4.117a.103.103 0 00-.104-.104zm-5.087 9.387H8.103v-.002A.103.103 0 018 23.282v-1.237a.1.1 0 01.103-.101h2.222c.042 0 .08-.027.097-.065l.619-1.524a.103.103 0 00-.023-.112l-1.554-1.554a.103.103 0 010-.145l.875-.875c.04-.04.105-.04.146 0l2.91 2.912a.102.102 0 01.011.135 5.947 5.947 0 00-1.078 2.583.102.102 0 01-.102.088zm12.058 1.736h4.124c.058 0 .104.046.104.103v1.237a.103.103 0 01-.104.103h-2.222a.105.105 0 00-.096.065l-.619 1.524a.103.103 0 00.023.112l1.553 1.554c.04.04.04.105 0 .145l-.875.875a.103.103 0 01-.145 0l-2.913-2.912a.104.104 0 01-.01-.135 5.946 5.946 0 001.078-2.583.102.102 0 01.102-.088zm-10.895 2.646a5.953 5.953 0 01-1.065-2.588.102.102 0 00-.101-.088h-4.12a.103.103 0 00-.103.104v1.236c0 .058.046.103.103.103H10.3c.042 0 .079.025.095.063l.64 1.516a.106.106 0 01-.023.114l-1.571 1.57a.103.103 0 000 .146l.875.875c.04.04.105.04.145 0l2.916-2.916a.104.104 0 00.011-.135h.002zm12.822-5.795h2.197c.058 0 .104.045.104.103v1.236a.102.102 0 01-.104.104H24.29a.102.102 0 01-.102-.088 5.953 5.953 0 00-1.064-2.588.104.104 0 01.01-.135l2.917-2.917c.04-.04.105-.04.145 0l.875.876c.04.04.04.105 0 .145L25.5 20.281a.106.106 0 00-.023.114l.64 1.515a.101.101 0 00.095.064zm-4.442 7.147a.104.104 0 01.135.01v.002l2.916 2.917c.04.04.04.105 0 .145l-.875.875a.102.102 0 01-.145 0l-1.572-1.57a.106.106 0 00-.114-.023l-1.515.64a.101.101 0 00-.063.095v2.196a.103.103 0 01-.103.103h-1.236a.102.102 0 01-.104-.103v-4.12c0-.05.037-.094.088-.101a5.954 5.954 0 002.588-1.065zM19.211 18.33c.955.152 1.834.53 2.583 1.078.042.03.098.026.135-.01l2.912-2.913a.102.102 0 000-.146l-.875-.875a.103.103 0 00-.145 0l-1.554 1.554a.103.103 0 01-.112.023l-1.524-.62a.105.105 0 01-.065-.096v-2.221a.103.103 0 00-.103-.104h-1.237a.103.103 0 00-.103.104v4.122c0 .051.037.095.088.102v.002zm-4.495 10.772A5.946 5.946 0 0017.3 30.18v.002c.051.007.088.05.088.102v4.123a.103.103 0 01-.104.103h-1.236a.103.103 0 01-.103-.103v-2.222a.105.105 0 00-.065-.097l-1.524-.619a.103.103 0 00-.112.023l-1.554 1.554a.103.103 0 01-.146 0l-.874-.875a.102.102 0 010-.146l2.912-2.913a.104.104 0 01.135-.01z"
        clipRule="evenodd"
      ></path>
      <path
        fill="#0D3151"
        fillRule="evenodd"
        d="M39.463 25.576l-.212-.005c-.536-.012-1.063-.024-1.603-.024-.61 0-1.218.014-1.815.03l-.682 1.872a3.596 3.596 0 00-.217 1.235c0 .217.03.464.088.682l-.092-.005a17.68 17.68 0 00-.822-.025c-.303 0-.624.014-.93.03l-.057-.088c.39-.726.783-1.466 1.146-2.38l2.41-5.921c.349-.842.465-1.161.61-1.815.32-.03.93-.132 1.263-.204.058.45.19.842.435 1.48l2.423 6.4c.509 1.321.914 2.061 1.19 2.526-.29-.03-.71-.03-1-.03-.378 0-.639.014-1.016.044a13.472 13.472 0 00-.624-2.366l-.493-1.436h-.002zm-1.741-5.006l-1.582 4.165c.493.03 1.002.03 1.51.03.509 0 1.001 0 1.51-.03l-1.436-4.165h-.002zm14.15 7.328a14.37 14.37 0 00-.421 1.277c-.886.26-1.843.377-2.932.377-3.004 0-5.224-2.119-5.224-5.254 0-3.135 2.497-5.268 5.994-5.268.9 0 1.9.146 2.714.45-.16.48-.319.988-.45 1.495h-.204c-.682-.798-1.466-1.133-2.627-1.133-2.365 0-3.831 1.626-3.831 4.253 0 2.626 1.625 4.527 4.063 4.527.93 0 1.9-.247 2.742-.855l.174.13.002.001zm9.474-3.453c-1.102-.03-2.03-.044-2.988-.044-.856 0-1.755.014-2.844.044v2.176c0 1.32.087 2.119.291 2.743a13.894 13.894 0 00-1.843 0c.02-.165.041-.308.061-.445.078-.537.142-.983.142-2.313V21.83c0-.712-.03-1.22-.101-1.51-.072-.305-.291-.45-.712-.45h-.146l.275-.611c.32.014.697.014 1.06.014.434 0 .841 0 1.103-.03a26.6 26.6 0 00-.116 2.627v1.596c.682.03 2.003.058 2.858.058a120 120 0 002.96-.058V22c0-1.32-.087-2.119-.29-2.743.29.014.58.014.885.014.305 0 .594 0 .9-.014-.116.9-.132 1.64-.132 2.322v5.036c0 1.32.087 2.119.29 2.743a17.92 17.92 0 00-.913-.03c-.319 0-.652.014-.943.03.131-.828.203-1.67.203-2.759v-2.155z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
};

export const AmexCard = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 70 48">
      <rect width="69" height="47" x="0.5" y="0.5" fill="#1F72CD" stroke="#C4C4C4" rx="5.5"></rect>
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M12.55 17L6 31.494h7.84l.973-2.311h2.221l.972 2.31h8.63V29.73l.77 1.764h4.464l.769-1.802v1.802h17.948l2.182-2.251 2.044 2.25 9.219.02-6.57-7.226L64.032 17h-9.076l-2.125 2.21L50.852 17H31.327l-1.677 3.74L27.934 17H20.11v1.704L19.24 17h-6.69zm26.802 2.058h10.306l3.153 3.405 3.254-3.405h3.152l-4.79 5.227 4.79 5.167h-3.295l-3.152-3.445-3.271 3.445H39.352V19.058zm2.545 4.052v-1.9h6.431l2.806 3.036-2.93 3.053h-6.307v-2.073h5.623V23.11h-5.623zm-27.83-4.052h3.821l4.344 9.828v-9.828h4.187l3.355 7.047 3.093-7.047h4.166v10.4h-2.535l-.02-8.15-3.696 8.15h-2.268l-3.716-8.15v8.15h-5.214l-.989-2.331h-5.341l-.986 2.33H9.473l4.592-10.399zm.099 5.913l1.76-4.153 1.757 4.153h-3.517z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
};

export const DiscoverCard = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 70 48">
      <rect width="69" height="47" x="0.5" y="0.5" fill="#fff" stroke="#C4C4C4" rx="5.5"></rect>
      <path fill="#FD6020" d="M29.468 47l39.5-11.5V42a5 5 0 01-5 5h-34.5z"></path>
      <path
        fill="#000"
        fillRule="evenodd"
        d="M61.471 19.222c2.173 0 3.368.967 3.368 2.793a2.614 2.614 0 01-2.173 2.794l2.934 3.976h-2.282l-2.499-3.868h-.217v3.868h-1.847v-9.563h2.716zm-.87 4.405h.544c1.195 0 1.739-.537 1.739-1.504 0-.86-.544-1.397-1.739-1.397h-.543v2.901zm-8.257 5.158h5.215v-1.612h-3.368v-2.579h3.26v-1.611h-3.26v-2.15h3.368v-1.611h-5.215v9.563zm-5.433-3.116l-2.499-6.447h-1.956L46.476 29h.979l4.02-9.778h-1.956l-2.608 6.447zm-22.057-1.612c0 2.687 2.173 4.943 4.89 4.943.869 0 1.63-.215 2.39-.537v-2.15c-.543.645-1.304 1.075-2.173 1.075-1.739 0-3.151-1.29-3.151-3.009v-.214c-.109-1.72 1.304-3.224 3.042-3.331.87 0 1.739.43 2.282 1.074v-2.149c-.652-.43-1.521-.537-2.282-.537-2.825-.215-4.998 2.041-4.998 4.835zm-3.368-1.182c-1.087-.43-1.413-.645-1.413-1.182.109-.645.652-1.182 1.304-1.074.543 0 1.087.322 1.521.752l.978-1.29c-.76-.644-1.738-1.074-2.716-1.074-1.521-.108-2.825 1.074-2.934 2.579v.107c0 1.29.543 2.042 2.282 2.579.434.108.87.322 1.304.537.326.215.543.538.543.967 0 .753-.652 1.397-1.304 1.397h-.108c-.87 0-1.63-.537-1.956-1.29l-1.195 1.183c.651 1.182 1.955 1.826 3.26 1.826 1.738.108 3.15-1.181 3.259-2.9v-.323c-.109-1.29-.652-1.934-2.825-2.794zm-6.302 5.91h1.847v-9.563h-1.847v9.563zM6.6 19.222h3.26c2.607.107 4.672 2.256 4.563 4.835 0 1.397-.652 2.687-1.738 3.654-.978.752-2.173 1.182-3.369 1.074H6.6v-9.563zm2.39 7.951c.87.108 1.848-.215 2.5-.752.651-.645.977-1.504.977-2.471 0-.86-.326-1.72-.978-2.364-.652-.538-1.63-.86-2.499-.752h-.543v6.34h.543z"
        clipRule="evenodd"
      ></path>
      <path
        fill="#FD6020"
        fillRule="evenodd"
        d="M37.675 19c-2.716 0-4.998 2.15-4.998 4.943 0 2.686 2.173 4.943 4.998 5.05 2.826.108 4.999-2.149 5.107-4.943-.108-2.794-2.281-5.05-5.106-5.05z"
        clipRule="evenodd"
      ></path>
    </svg>
  );
};

{
  /* EXAMPLE USAGE
<Grid container spacing={2}>
  <Grid item sm={6} md={4}>
    <PaymentCard ePayName="John Smith" ePayType="visa" />
  </Grid>
  <Grid item sm={6} md={4}>
    <PaymentCard ePayName="John Smith" ePayType="mastercard" />
  </Grid>
</Grid>
 */
}

const PaymentCard = (props: PaymentCardProps): JSX.Element => {
  const classes = useStyles();
  const { ePayNickName, ePayName, ePayType, cardEnding, goodThru, onEdit, accountType, accountAliasId, onDelete } =
    props;

  const cardExpiryDate = useMemo(() => {
    if (goodThru?.length !== 4) {
      return {
        expiryDate: 'MM/YY',
        isExpired: true,
      };
    }

    const month = goodThru.slice(0, 2);
    const year = goodThru.slice(2, 4);
    const isExpired = +new Date() > +new Date(Number(`20${year}`), Number(`${Number(month) - 1}`), 1);
    return {
      expiryDate: `${month}/${year}`,
      isExpired,
    };
  }, [goodThru]);

  const onPaymentEdit = () => {
    onEdit?.(accountAliasId, ePayType?.toUpperCase() == 'ACH');
  };

  const onPaymentDelete = () => {
    onDelete?.(accountAliasId, ePayType?.toUpperCase() == 'ACH', ePayName as string, ePayType as string);
  };

  return (
    <Card className={classes.cardWrap} {...props}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          {ePayType?.toUpperCase() == 'VISA' && <VisaCard />}
          {ePayType?.toUpperCase() == 'MASTERCARD' && <MasterCard />}
          {ePayType?.toUpperCase() == 'ACH' && <ACH />}
          {ePayType?.toUpperCase() == 'AMERICANEXPRESS' && <AmexCard />}
          {ePayType?.toUpperCase() == 'DISCOVER' && <DiscoverCard />}
          <div>
            <AgentNetButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={onPaymentEdit}
              className={classes.editBtn}
            >
              Edit
            </AgentNetButton>
            <AgentNetButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={onPaymentDelete}
              className={clsx(classes.editBtn, classes.deleteBtn)}
            >
              Delete
            </AgentNetButton>
          </div>
        </div>
        {ePayName && (
          <Typography variant="h3" className={classes.mb1}>
            {ePayName}
          </Typography>
        )}
        {ePayNickName && (
          <Typography variant="body1" color="textSecondary">
            {ePayNickName}
          </Typography>
        )}
        {ePayType?.toUpperCase() !== 'ACH' ? (
          <Typography variant="body1" color="textSecondary" className={classes.accEnding}>
            Card Ending in {cardEnding ? cardEnding.slice(-4) : 'XXXX'} |{' '}
            <span
              className={clsx({
                [classes.error]: cardExpiryDate.isExpired,
              })}
            >
              Good Thru {cardExpiryDate.expiryDate}
            </span>
          </Typography>
        ) : (
          <Typography variant="body1" color="textSecondary" className={classes.accEnding}>
            {accountType} | Acc. Ending in {cardEnding ? cardEnding.slice(-4) : 'XXXX'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentCard;
